import { Loader2, RotateCcw, Save } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useDashboardWidgets } from "@/hooks/useDashboard";
import { useModuli } from "@/hooks/useModuli";
import { SEZIONI_M1, SEZIONI_M2, SEZIONI_M3 } from "@/lib/nav-config";
import { cn } from "@/lib/utils";
import type { PermessoRiga, RisorsaTipo } from "@/types/api";

interface PermessoOpzione {
  key: string;
  label: string;
  descrizione?: string;
}

interface SezioneSpec {
  tipo: RisorsaTipo;
  titolo: string;
  descrizione: string;
  opzioni: PermessoOpzione[];
}

const NAV_OPZIONI: PermessoOpzione[] = [
  { key: "bacheca", label: "Bacheca", descrizione: "Voci, decisioni, blocchi" },
  { key: "documenti", label: "Documenti", descrizione: "Documenti progetto" },
  { key: "conversazioni", label: "Conversazioni", descrizione: "Registro chiamate/email/incontri" },
  { key: "attivita", label: "Attività", descrizione: "Feed avanzamento lavori" },
  { key: "moduli", label: "Moduli", descrizione: "Elenco moduli (M1, M2, ...)" },
];

const BACHECA_SCOPE_OPZIONI: PermessoOpzione[] = [
  {
    key: "vedi_tutte",
    label: "Vede tutte le voci di bacheca",
    descrizione:
      "OFF = vede solo le voci rivolte a lui (assegnato_a = utente). ON = vede tutte le voci visibili al cliente, come Presidenza/Management.",
  },
];

const AZIONI_OPZIONI: PermessoOpzione[] = [
  {
    key: "upload_dati",
    label: "Upload dati operativi (M1)",
    descrizione:
      "Carica i file Excel dei dati operativi (ordini, giacenze, piano, anagrafiche). Default OFF: va concesso esplicitamente — i ruoli non configurati NON lo ereditano.",
  },
];

type RigheMap = Map<string, boolean>; // chiave: `${tipo}:${key}`

function rigaKey(tipo: RisorsaTipo, key: string): string {
  return `${tipo}:${key}`;
}

function rigaToMap(righe: PermessoRiga[]): RigheMap {
  return new Map(righe.map((r) => [rigaKey(r.risorsa_tipo, r.risorsa_key), r.abilitato]));
}

function mapToRighe(map: RigheMap): PermessoRiga[] {
  return Array.from(map.entries()).map(([k, abilitato]) => {
    const [tipo, key] = k.split(":") as [RisorsaTipo, string];
    return { risorsa_tipo: tipo, risorsa_key: key, abilitato };
  });
}

interface PermessiMatriceProps {
  /** Righe attualmente persistite (preset ruolo o override utente). */
  righeIniziali: PermessoRiga[];
  /** Mode: "preset" = sta editando un preset ruolo; "override" = override utente. */
  mode: "preset" | "override";
  /** Solo per override: preset di ruolo ereditato (read-only, mostrato come baseline). */
  presetRuolo?: PermessoRiga[];
  /** Salva. Riceve la lista completa di righe nuovo stato. */
  onSave: (righe: PermessoRiga[]) => Promise<unknown>;
  /** True se la mutation è pending. */
  saving?: boolean;
  /** Disabilita tutto (es. utente non ancora loggato → niente override personale). */
  disabled?: boolean;
}

export function PermessiMatrice({
  righeIniziali,
  mode,
  presetRuolo = [],
  onSave,
  saving = false,
  disabled = false,
}: PermessiMatriceProps) {
  const [stato, setStato] = useState<RigheMap>(() => rigaToMap(righeIniziali));
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setStato(rigaToMap(righeIniziali));
    setDirty(false);
  }, [righeIniziali]);

  const presetMap = useMemo(() => rigaToMap(presetRuolo), [presetRuolo]);

  const { data: widgets = [] } = useDashboardWidgets();
  const { data: moduli = [] } = useModuli();

  const sezioni: SezioneSpec[] = useMemo(
    () => [
      {
        tipo: "dashboard_widget",
        titolo: "Widget Dashboard",
        descrizione:
          "Quali widget l'utente vede in /dashboard. La pagina Dashboard è sempre raggiungibile.",
        opzioni: widgets.map((w) => ({
          key: w.codice,
          label: w.titolo,
          descrizione: w.descrizione ?? undefined,
        })),
      },
      {
        tipo: "nav_voce",
        titolo: "Sezioni Progetto",
        descrizione:
          "Voci visibili nel menu Progetto. Panoramica è sempre visibile.",
        opzioni: NAV_OPZIONI,
      },
      {
        tipo: "modulo",
        titolo: "Accesso ai moduli",
        descrizione:
          "Per ogni modulo, se l'utente può aprirlo. Se non abilitato, in /moduli appare con badge 'accesso non abilitato'.",
        opzioni: moduli
          .filter((m) => m.tipo === "modulo_funzionale")
          .map((m) => ({
            key: m.codice,
            label: `${m.codice} — ${m.nome}`,
            descrizione: m.sottotitolo ?? undefined,
          })),
      },
      {
        // Stesso `tipo` di «Sezioni Progetto» (le chiavi sono `nav_voce.m3.*`):
        // per l'utente sono due cose diverse, per il modello dei permessi no.
        tipo: "nav_voce",
        titolo: "Sezioni dei moduli",
        descrizione:
          "Dentro un modulo, quali sezioni vede. «Non spec.» = eredita il permesso del modulo, che è il comportamento di sempre: metti «No» solo per togliere una singola sezione. Se ne resta una sola, cliccando il modulo si atterra lì.",
        opzioni: [
          ...SEZIONI_M1.map((s) => ({
            key: s.key,
            label: `M1 · ${s.label}`,
            descrizione: undefined,
          })),
          ...SEZIONI_M2.map((s) => ({
            key: s.key,
            label: `M2 · ${s.label}`,
            descrizione: undefined,
          })),
          ...SEZIONI_M3.map((s) => ({
            key: s.key,
            label: `M3 · ${s.label}`,
            descrizione: undefined,
          })),
        ],
      },
      {
        tipo: "bacheca_scope",
        titolo: "Bacheca",
        descrizione:
          "Comportamento della bacheca per questo profilo.",
        opzioni: BACHECA_SCOPE_OPZIONI,
      },
      {
        tipo: "azione",
        titolo: "Azioni operative",
        descrizione:
          "Azioni sensibili su dati e operatività. Default-deny: a differenza degli altri permessi, NON sono ereditate dai ruoli non ancora configurati — vanno concesse esplicitamente.",
        opzioni: AZIONI_OPZIONI,
      },
    ],
    [widgets, moduli]
  );

  function setRiga(tipo: RisorsaTipo, key: string, abilitato: boolean | null) {
    setStato((prev) => {
      const next = new Map(prev);
      const k = rigaKey(tipo, key);
      if (abilitato === null) {
        next.delete(k);
      } else {
        next.set(k, abilitato);
      }
      return next;
    });
    setDirty(true);
  }

  function resetAll() {
    setStato(new Map());
    setDirty(true);
  }

  async function handleSave() {
    const righe = mapToRighe(stato);
    await onSave(righe);
    setDirty(false);
  }

  return (
    <div className="space-y-6">
      {sezioni.map((sez) => (
        <section
          key={sez.titolo}
          className={cn(
            "rounded-lg border bg-card divide-y",
            disabled && "opacity-60 pointer-events-none"
          )}
        >
          <header className="px-4 py-3 bg-muted/30">
            <h3 className="text-sm font-semibold">{sez.titolo}</h3>
            <p className="text-xs text-muted-foreground mt-0.5">{sez.descrizione}</p>
          </header>
          {sez.opzioni.length === 0 ? (
            <p className="px-4 py-3 text-xs text-muted-foreground italic">
              Nessuna opzione disponibile.
            </p>
          ) : (
            sez.opzioni.map((opz) => {
              const k = rigaKey(sez.tipo, opz.key);
              const valore = stato.get(k);
              const preset = presetMap.get(k);
              return (
                <Riga
                  key={opz.key}
                  opzione={opz}
                  valore={valore}
                  preset={mode === "override" ? preset : undefined}
                  mode={mode}
                  onChange={(v) => setRiga(sez.tipo, opz.key, v)}
                />
              );
            })
          )}
        </section>
      ))}

      <footer className="flex items-center justify-between gap-3 pt-2">
        <Button
          variant="outline"
          onClick={resetAll}
          disabled={disabled || saving}
          title={
            mode === "preset"
              ? "Cancella tutte le righe preset (il ruolo torna a 'non configurato', default legacy = tutto abilitato)"
              : "Cancella tutti gli override personali (l'utente torna a usare solo il preset del suo ruolo)"
          }
        >
          <RotateCcw className="size-3.5" />
          Azzera{" "}
          {mode === "preset" ? "preset ruolo" : "override personali"}
        </Button>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {dirty && <span>Modifiche non salvate</span>}
          <Button onClick={handleSave} disabled={disabled || saving || !dirty}>
            {saving ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <Save className="size-3.5" />
            )}
            Salva
          </Button>
        </div>
      </footer>
    </div>
  );
}

interface RigaProps {
  opzione: PermessoOpzione;
  valore: boolean | undefined;
  preset: boolean | undefined;
  mode: "preset" | "override";
  onChange: (v: boolean | null) => void;
}

function Riga({ opzione, valore, preset, mode, onChange }: RigaProps) {
  // Stati visibili:
  //   - "Non specificato" → valore undefined → eredita default (preset/legacy)
  //   - "Abilitato"
  //   - "Disabilitato"
  // Tre pulsanti compatti: "Non spec." · Sì · No.
  return (
    <div className="px-4 py-2.5 flex items-center gap-3">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium leading-tight">{opzione.label}</p>
        {opzione.descrizione && (
          <p className="text-xs text-muted-foreground mt-0.5">{opzione.descrizione}</p>
        )}
        {mode === "override" && preset !== undefined && (
          <p className="text-[11px] text-muted-foreground mt-1">
            Preset ruolo:{" "}
            <Badge
              variant="outline"
              className={cn(
                "text-[10px] ml-1",
                preset
                  ? "bg-success/10 text-success border-success/30"
                  : "bg-muted text-muted-foreground"
              )}
            >
              {preset ? "abilitato" : "disabilitato"}
            </Badge>
          </p>
        )}
      </div>
      <div className="inline-flex rounded-md border bg-background overflow-hidden text-xs shrink-0">
        <TriButton
          active={valore === undefined}
          onClick={() => onChange(null)}
          label={mode === "override" ? "Eredita" : "Non spec."}
        />
        <TriButton
          active={valore === true}
          onClick={() => onChange(true)}
          label="Sì"
          tone="success"
        />
        <TriButton
          active={valore === false}
          onClick={() => onChange(false)}
          label="No"
          tone="destructive"
        />
      </div>
    </div>
  );
}

function TriButton({
  active,
  onClick,
  label,
  tone,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  tone?: "success" | "destructive";
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "px-2.5 py-1 transition-colors border-l first:border-l-0",
        active && tone === "success" && "bg-success/15 text-success",
        active && tone === "destructive" && "bg-destructive/10 text-destructive",
        active && !tone && "bg-muted text-foreground",
        !active && "text-muted-foreground hover:bg-muted/40"
      )}
    >
      {label}
    </button>
  );
}
